import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { setEmailGridData } from "../../../../actions/taskSidebar";
import { number } from "../../../../config";
import { getAllEmailsForTask } from "../../../../shared/services/workflowEmails.service";
import { GridContext } from "../../../../utils/kendo";
import EmailDescription from "./EmailDescription";
import { CreatedOn, Reply, Sender, Subject, emailAction, useTeamEmailTaskSidebar } from "./EmailGridCells";
import EmailHeader from "./EmailHeader";
import EmailPopUpSidebar from "./EmailPopUpSidebar";
import "./TaskEmail.scss";
import { email_label } from "./TaskEmailConstant";

/**
 * TaskEmail component for displaying and managing emails in the task sidebar
 * @param {Object} props - Component props
 * @param {Function} props.setOffset - Function to set offset for pagination
 * @param {number} props.offset - Current offset value
 * @author Sejal
 */
const TaskEmail = ({ setOffset, offset, setCurrentEmailData, currentEmailData, resetScroller }) => {
  const dispatch = useDispatch();
  const { emailGridData, task } = useSelector((state) => state.taskSidebar);
  const [searchQuery, setSearchQuery] = useState("");
  const [hover, setHover] = useState({ value: number.ZERO });
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [showEmailDescPopup, setShowEmailDescPopup] = useState(false);
  const [selectedEmailData, setSelectedEmailData] = useState(null);
  const [filteredGridData, setFilteredGridData] = useState([]);

  /**
   * Cleans HTML content for search functionality
   * @param {string} content - The HTML content to clean
   * @returns {string} The cleaned text content
   * @author Sejal
   */
  const cleanContent = (content) => {
    if (!content) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    let cleaned = tempDiv.textContent || tempDiv.innerText;
    cleaned = cleaned.replace(/\s+/g, " ");
    return cleaned.trim();
  };

  // Update filtered data whenever search query or emailGridData changes
  useEffect(() => {
    if (!emailGridData) return;

    if (searchQuery.trim() === "") {
      setFilteredGridData(emailGridData);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = emailGridData.filter((item) => {
        if (!item) return false;
        const subject = cleanContent(item?.Subject || "");
        const senderName = (item?.SenderName || "").toLowerCase();
        const sender = (item?.Sender || "").toLowerCase();
        const partialContent = cleanContent(item?.PartialContent || "");
        return (
          subject.toLowerCase().includes(query) ||
          senderName.includes(query) ||
          sender.includes(query) ||
          partialContent.toLowerCase().includes(query)
        );
      });
      setFilteredGridData(filtered);
    }
  }, [searchQuery, emailGridData]);

  /**
   * Handles refresh action for email grid
   * @author Sejal
   */
  const handleRefresh = () => {
    if (task?.taskId) {
      dispatch(
        getAllEmailsForTask({
          limit: number.FIVE,
          offset: number.ZERO,
          taskId: task.taskId,
        })
      );
      if (resetScroller) {
        resetScroller();
      }
    }
  };

  /**
   * Handles search functionality
   * @param {string} query - The search query
   * @author Sejal
   */
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  /**
   * Updates the grid data
   * @param {Array} updatedData - The updated grid data
   * @author Sejal
   */
  const handleGridDataUpdate = (updatedData) => {
    dispatch(setEmailGridData(updatedData));
  };

  const { customRowRender, scrollHandler } = useTeamEmailTaskSidebar({
    task,
    hover,
    selectedEmailId,
    setGridData: handleGridDataUpdate,
    setOffset: setOffset,
    offset: offset,
    setSelectedEmailId,
    setHover,
    setShowEmailDescPopup,
    setSelectedEmailData,
    setCurrentEmailData,
    currentEmailData,
    resetScroller,
  });

  return (
    <>
      <div>
        <EmailHeader handleRefresh={handleRefresh} handleSearch={handleSearch} />
        <div>
          <GridContext.Provider
            value={{
              gridData: filteredGridData,
              setGridData: handleGridDataUpdate,
              hover,
            }}>
            <Grid
              data={filteredGridData}
              sortable={true}
              className='team-email-grid-task-sidebar'
              header={false}
              onScroll={scrollHandler}
              fixedScroll={true}
              rowRender={customRowRender}>
              <GridColumn field='attachmentCount' cell={Reply} width={80} />
              <GridColumn field='SenderName' cell={Sender} width={100} />
              <GridColumn field='Subject' cell={Subject} width={300} />
              <GridColumn field='CreatedOn' cell={CreatedOn} width={80} />
            </Grid>
          </GridContext.Provider>
        </div>
      </div>
    </>
  );
};

export default TaskEmail;
